import { useState } from "react";
import {
  Typography,
  TextField,
  Button,
  Box,
  IconButton,
  InputAdornment,
  FormControl,
  MenuItem,
  Select,
} from "@mui/material";
import { useFormik } from "formik";
import { signUpschema } from "../schema/schema";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { roles } from "../utils/Data";
import { registerProfile } from "../../services/services";
import { useNavigate } from "react-router-dom";
import RoleSelectionModal from "../modal/RoleSelectionModal";
import { useSnackbar } from "../utils/SnackBar";

function SignUp() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const showSnackbar = useSnackbar();
  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword(!showConfirmPassword);
  const navigate = useNavigate();
  const [openRoleModal, setOpenRoleModal] = useState(true);
  const [, setSelectedRole] = useState<string>("");

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      role: "",
    },
    validationSchema: signUpschema,
    onSubmit: (values) => {
      if (values.password === values.confirmPassword) {
        let payload: any = { ...values, role: values.role.toLowerCase() };
        delete payload.confirmPassword;
        console.log(payload);
        registerProfile(payload)
          .then((result: any) => {
            showSnackbar(result?.data?.msg);
            navigate("/signup-confirmation");
          })
          .catch((error) => {
            showSnackbar(
              error?.response?.data?.message || error?.response?.data?.msg,
            );
          });
      } else {
        showSnackbar("password and confirm password are not matching");
      }
    },
  });

  const handleSetRole = (role: string) => {
    setSelectedRole(role);
    formik.setFieldValue("role", role);
  };

  const handleModalClose = () => {
    setOpenRoleModal(false);
  };

  return (
    <Box>
      <RoleSelectionModal
        open={openRoleModal}
        onClose={handleModalClose}
        setRole={handleSetRole}
      />
      <Typography
        variant="h5"
        align="center"
        sx={{
          color: "#321548",
          fontWeight: 700,
          mb: 2,
          fontSize: { xs: "1rem", sm: "1.5rem" },
        }}
      >
        Welcome to Global Genius Index (G2I)
      </Typography>
      <Typography
        variant="subtitle1"
        align="center"
        sx={{ color: "#746283", fontWeight: 400, mb: 4 }}
      >
        Create your account and Discover teacher within you
      </Typography>
      <Box component="form" onSubmit={formik.handleSubmit}>
        <TextField
          className="input-root2"
          placeholder="Full Name"
          name="name"
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
          variant="outlined"
          fullWidth
          sx={{ mb: 2, background: "white", borderRadius: 3 }}
        />
        <TextField
          className="input-root2"
          placeholder="Email"
          name="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          variant="outlined"
          fullWidth
          sx={{ mb: 2, background: "white", borderRadius: 3 }}
        />
        <TextField
          className="input-root2"
          placeholder="Password"
          name="password"
          type={showPassword ? "text" : "password"}
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password && formik.errors.password}
          variant="outlined"
          fullWidth
          sx={{ mb: 2, background: "white", borderRadius: 3 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          className="input-root2"
          placeholder="Re-enter Password"
          name="confirmPassword"
          type={showConfirmPassword ? "text" : "password"}
          value={formik.values.confirmPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.confirmPassword &&
            Boolean(formik.errors.confirmPassword)
          }
          helperText={
            formik.touched.confirmPassword && formik.errors.confirmPassword
          }
          variant="outlined"
          fullWidth
          sx={{ mb: 2, background: "white", borderRadius: 3 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle confirm password visibility"
                  onClick={handleClickShowConfirmPassword}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <FormControl
          variant="outlined"
          fullWidth
          className="input-root2"
          sx={{ mb: 2, background: "white", borderRadius: 3 }}
        >
          {/* <InputLabel>Automatically selected role</InputLabel> */}
          <Select
            placeholder="Automatically selected role"
            name="role"
            value={formik.values.role}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.role && Boolean(formik.errors.role)}
            displayEmpty
            renderValue={(selected: any) => {
              if (!selected) {
                return "User Role";
              }
              return selected;
            }}
          >
            {roles.map(
              (role: any, index: number) => (
                // role !== "ADMIN" && (
                <MenuItem key={index} value={role}>
                  {role}
                </MenuItem>
              ),
              // )
            )}
          </Select>
          {formik.touched.role && formik.errors.role && (
            <Typography color="error" variant="caption">
              {formik.errors.role}
            </Typography>
          )}
        </FormControl>

        <Button
          variant="contained"
          fullWidth
          sx={{
            mt: 2,
            backgroundColor: "#35185A",
            borderRadius: 3,
            textTransform: "none",
            height: "50px",
          }}
          type="submit"
          disabled={!(formik.isValid && formik.dirty)}
        >
          Create Account
        </Button>
      </Box>
    </Box>
  );
}

export default SignUp;
